import { Component, computed, effect, HostListener, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import FormService, { FieldDefinition } from '../forms.service';
import { map } from 'rxjs';
import { CommonModule } from '@angular/common';
import EmployeeService from '../../services/employee.service';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { SignaturePadComponent } from '../../components/signature-field/signature-field.component';
import { ElementList } from '../components/element-list/element-list';
import { InputCheckbox } from '../components/input-checkbox/input-checkbox';
import { InputText } from '../components/input-text/input-text';
import { NextFormService } from '../../services/next-form.service';
import ClientService from '../../services/client.service';
import { Layout } from '../../layout/layout';
import NotificationService from '../../services/notification.service';
import ThematiqueService from '../../services/thematique.service';
import CurrentFormService from '../current_form.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { toSignal } from '@angular/core/rxjs-interop';
import { RatingComponent } from '../components/rating-component/rating.component';
import SousTraitantService from '../../services/sous_traitant.service';
import { EpiSelectorComponent } from '../../components/epi-selector/epi-selector.component';
import { EmployeeSelectFieldComponent } from '../../components/employee-select-field/employee-select-field.component';

@Component({
  selector: 'app-show',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SignaturePadComponent,
    ElementList,
    InputCheckbox,
    InputText,
    Layout,
    NgSelectModule,
    RatingComponent,
    EpiSelectorComponent,
    EmployeeSelectFieldComponent,
  ],
  templateUrl: './show.html',
  styleUrl: './show.css',
  standalone: true,
})
export class Show implements OnInit {
  protected form = inject(CurrentFormService);
  private route = inject(ActivatedRoute);
  private formService = inject(FormService);
  private router = inject(Router);
  private nextFormService = inject(NextFormService);
  private employeeService = inject(EmployeeService);
  private notificationService = inject(NotificationService);
  private clientService = inject(ClientService);
  private sousTraitantService = inject(SousTraitantService);
  private thematiqueService = inject(ThematiqueService);
  showExternals = false;
  protected stepId = toSignal(
    this.route.params.pipe(map((params) => Number.parseInt(params['stepId']))),
    {
      initialValue: 1,
    },
  );
  private responseId!: string;
  employees$ = this.employeeService.all();
  clients$ = this.clientService.all();
  sousTraitants$ = this.sousTraitantService.all();
  thematiques$ = this.thematiqueService.all();

  stepTitle = signal<string>('');

  public photos: { [key: string]: { file: File; url: string }[] } = {};

  notificationId = signal<string | undefined>(undefined);

  public loadedData = signal<{
    employee: { name: string; email: string; phone: string; organizationUuid: string };
  }>({
    employee: { name: '', email: '', phone: '', organizationUuid: '' },
  });

  loadedEmployees = signal<
    { _id: string; name: string; email: string; phone: string; organizationUuid: string }[]
  >([]);

  public currentFields = computed(() => {
    const id = this.stepId();
    const definition = this.form.definition();
    if (!id || !definition) return [];
    return this.form.getFieldsForStep(id);
  });

  getEmployeesToSign() {
    return this.form.formGroup.get('employees_uuid')?.value;
  }

  constructor() {
    effect(() => {
      // console.log('--- LE SIGNAL A CHANGÉ EN DIRECT STEP TITLE : ---', this.stepTitle());
    });
  }

  onPhotoAdded(fieldName: string, event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const filesArray = Array.from(input.files);

      if (!this.photos[fieldName]) this.photos[fieldName] = [];

      filesArray.forEach((file: File) => {
        const objectUrl = URL.createObjectURL(file);

        this.photos[fieldName].push({
          file: file,
          url: objectUrl,
        });
      });

      input.value = '';
    }
  }

  onRemovePhoto(fieldName: string, index: number) {
    const photosArray = this.photos[fieldName];

    if (photosArray && photosArray[index]) {
      URL.revokeObjectURL(photosArray[index].url);

      photosArray.splice(index, 1);
    }
  }

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;

    const hasResponseId = params.has('responseId');
    const responseId = params.get('responseId');
    const formDefId = params.get('id');
    const stepId = params.get('stepId');

    if (!hasResponseId && formDefId) {
      this.form.reset();
      this.formService.startForm(formDefId).subscribe({
        next: (resp) => {
          return this.router.navigate(['/forms', formDefId, resp, 1], {
            queryParamsHandling: 'preserve',
          });
        },
        error: () => {
          return this.router.navigate(['/forms', formDefId], { queryParamsHandling: 'preserve' });
        },
      });
    } else {
      this.responseId = responseId!;

      if (formDefId && stepId) this.form.loadForm(formDefId);
    }

    this.route.queryParamMap.subscribe((params) => {
      const notificationId = params.get('notification_id');

      if (!notificationId) return;

      this.notificationService.getNotification(notificationId).subscribe((notification) => {
        this.form.notification.set(notification[0]);
        Object.keys(this.form.form.controls).forEach((field) => {
          if (field === 'employee_uuid' || field === 'employee' || field === 'employees_uuid') {
            if ((notification[0] as any).notificationType === 'causerie:scheduled') {
              const currentEmp = notification[0].payload.employees.find((e: any) => e.selected);
              this.employeeService.get(currentEmp._id).subscribe({
                next: (emp) => {
                  this.loadedData.set({
                    employee: {
                      name: `${emp[0].firstname} ${emp[0].lastname}`,
                      email: emp[0].email,
                      phone: emp[0].phone,
                      organizationUuid: emp[0].organizationUuid,
                    },
                  });
                },
              });
            }
            if ((notification[0] as any).notificationType !== 'causerie:scheduled') {
              const value = notification[0].payload.employee.uuid;
              this.employeeService.get(value).subscribe({
                next: (emp) => {
                  this.form.get(field)?.setValue({
                    ...emp[0],
                    name: `${emp[0].firstname} ${emp[0].lastname}`,
                  });
                  this.loadedData.set({
                    employee: {
                      name: `${emp[0].firstname} ${emp[0].lastname}`,
                      email: emp[0].email,
                      phone: emp[0].phone,
                      organizationUuid: emp[0].organizationUuid,
                    },
                  });
                },
              });
            }
          } else if (field.includes('client')) {
            this.form.get(field)?.setValue({
              ...notification[0].payload.client,
            });

            this.form.loadClient(notification[0].payload.client.uuid);
          }

          if (field === 'employees_uuid') {
            const notif = notification[0];

            if ((notification[0] as any).notificationType !== 'causerie:scheduled')
              this.employeeService.get(notif.payload.employees[0].uuid).subscribe((res) => {
                this.loadedData.set({
                  employee: {
                    name: res[0].firstname + ' ' + res[0].lastname,
                    email: res[0].email,
                    phone: res[0].phone,
                    organizationUuid: res[0].organizationUuid,
                  },
                });
              });
            this.form
              .get(field)
              ?.patchValue([
                ...notif.payload.suggestions,
                notif.payload.employees.find((e: any) => e.selected),
              ]);

            this.employeeService
              .findByUuids(notif.payload.employees.map((e: any) => e._id))
              .subscribe({
                next: (emps) => {
                  this.loadedEmployees.set(
                    emps.map((e) => ({
                      _id: e._id,
                      email: e.email,
                      name: e.name || e.firstname + ' ' + e.lastname,
                      organizationUuid: e.organizationUuid,
                      phone: e.phone,
                    })),
                  );
                },
              });
            this.loadedEmployees.set([
              ...notif.payload.suggestions,
              notif.payload.employees.find((e: any) => e.selected),
            ]);
          }
        });
        this.notificationId.set(notificationId);
      });
    });
  }

  getExternals(fieldName: string) {
    return this.form.get(fieldName)?.value || [];
  }
  addExternal(fieldName: string) {
    const inputValue = this.form.get('input_' + fieldName);
    const inputForm = this.form.get(fieldName) as FormArray;
    console.log(
      `form external : ${fieldName} adding to input : ${'input_' + fieldName} value : ${inputValue}`,
    );

    if (!inputValue || !inputForm) return;

    inputForm.push(new FormControl(inputValue.value.trim()));
    inputValue.reset();
  }

  removeExternal(fieldName: string, index: number) {
    const inputForm = this.form.get(fieldName) as FormArray;

    inputForm.removeAt(index);
  }

  externalAddSignature(fieldName: string, item: string, signature: any) {
    const input = this.form.get(fieldName) as FormArray;

    if (!input) return;

    input.push(
      new FormControl({
        signature,
        name: item,
      }),
    );
  }

  onCheckboxCheck(fieldName: string, value: string, event: Event) {
    const values = this.form.get(fieldName) as FormArray;
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      values.push(new FormControl(value));
    } else {
      const index = values.controls.findIndex((el, index) => el.value === value);

      if (index !== -1) values.removeAt(index);
    }
  }

  addSignatureToForm(signature: any, employee: any) {
    const control = this.form.get('employees');

    if (!control) return;

    const savedSignatures = Array.isArray(control.value) ? control.value : [];

    savedSignatures.push({
      _id: employee._id,
      name: employee.name,
      signature,
    });

    control.setValue(savedSignatures);
  }

  clientChange() {
    const client = this.form.get('client')?.value;
    this.clientService.get(client._id).subscribe({
      next: (client) => {
        console.log(client);
        this.form.client.set(client);
      },
    });
  }

  changeEmployees() {
    const employees = this.form.get('employees_uuid')!.value;

    this.employeeService.findByUuids(employees.map((e: any) => e._id)).subscribe({
      next: (res) => {
        this.loadedEmployees.set(
          res.map((e) => ({
            _id: e._id,
            email: e.email,
            name: e.name || e.firstname + ' ' + e.lastname,
            organizationUuid: e.organizationUuid,
            phone: e.phone,
          })),
        );
      },
    });
  }

  get currentStepTitle(): string {
    const definition = this.form?.definition?.();
    const index = this.stepId() - 1;

    // On vérifie que la définition, les étapes et l'étape à l'index existent
    if (definition?.steps && definition.steps[index]) {
      return definition.steps[index].title || '';
    }

    return '';
  }

  goToNextStep() {
    if (this.form?.form.contains('employee')) {
      const input = this.form.get('employee');
      const employeeValue = input?.value;

      if (employeeValue && typeof employeeValue === 'object' && !employeeValue.organizationUuid) {
        const clientOrganizationUuid = this.form.get('client')?.value?.organizationUuid;

        input.setValue({
          ...employeeValue,
          organizationUuid: clientOrganizationUuid,
        });
      }
    }
    this.form.get('');

    this.formService.saveData(
      this.responseId,
      this.form.value,
      this.form.definition()!.steps.length === this.stepId(),
      this.form.definition()!._id,
      this.photos,
    );

    if (this.form.definition()!.steps.length > this.stepId()) {
      let nextstep = this.stepId() + 1;
      const formDefinition = this.form.definition();
      const step = formDefinition?.steps.find((step) => step.id === this.stepId());

      const fieldWithLogic = step?.fields.filter((e) => e.logic)[0];
      if (fieldWithLogic) {
        switch (fieldWithLogic.type) {
          case 'input:switch':
            if (
              this.form.get(fieldWithLogic?.name)?.value &&
              fieldWithLogic?.logic?.Oui?.goto_step
            ) {
              nextstep = fieldWithLogic?.logic.Oui.goto_step;
            } else {
              if (fieldWithLogic?.logic?.Non?.goto_step)
                nextstep = fieldWithLogic?.logic.Non.goto_step;
            }
            break;

          case 'input:radio':
            const valueIndex = this.form.get(fieldWithLogic?.name)?.value;
            const value = fieldWithLogic.items![valueIndex];
            if (
              fieldWithLogic.logic &&
              fieldWithLogic.logic[value] &&
              fieldWithLogic.logic[value].goto
            ) {
              nextstep = fieldWithLogic.logic[value].goto;
            }
            break;
        }
      }

      const futureStep = formDefinition?.steps.find((step) => step.id === nextstep);
      if (futureStep?.logic && futureStep.logic.skip) {
        if (futureStep.logic.skip.condition.includes('!')) {
          const fieldNameToBeTrue = futureStep.logic.skip.condition.replace('!', '');
          const fieldValue = this.form.get(fieldNameToBeTrue)?.value;

          if (!fieldValue) {
            nextstep = futureStep.logic.skip.goto_step;
          }
        }
      }

      this.navigateToStep(nextstep);
      this.router.navigate(['/forms', this.form.definition()?._id, this.responseId, nextstep]);
    } else {
      const nextFormsIds = this.form.definition()!.nextForms;
      if (nextFormsIds) this.nextFormService.setNextForms(nextFormsIds);
      if (this.notificationId()) this.notificationService.done(this.notificationId()!);
      this.router.navigate(['/forms/success']);
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  private navigateToStep(stepId: number) {
    this.form.goToStep(stepId);
    this.router.navigate(['/forms', this.form.definition()?._id, this.responseId, stepId]);
  }

  logic(field: FieldDefinition) {
    if (field.action) {
      if (field.action.type === 'goto') {
        const d = { ...this.form.value };
        d[field.name] = true;
        this.formService.saveData(
          this.responseId,
          d,
          this.form.definition()!.steps.length === this.stepId(),
          this.form.definition()!._id,
          this.photos,
        );
        if (field.action.step) this.navigateToStep(Number.parseInt(field.action.step));
      }
    }
  }

  compareValueSelect(value: { uuid: string }, defaultValue: { uuid: string }) {
    return value.uuid === defaultValue.uuid;
  }

  compareEmployees(item1: any, item2: any): boolean {
    return item1._id === item2.uuid;
  }

  confirmExit(event: Event) {
    const ok = confirm('Vous allez perdre toutes les informations rentrées dans le formulaire.');
    if (!ok) {
      event.preventDefault(); // Annule la redirection du routerLink si l'utilisateur clique sur "Annuler"
    }
  }

  // 2. Déclenché lors d'un retour arrière physique/swipe mobile ou fermeture/rafraîchissement d'onglet
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    $event.returnValue = true;
  }
}
